import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { getMaxAllowedPastDate, isTodayOrFutureInputDate } from './helpers';

const DEFAULT_VALUES = {
	departureDate: '',
	departureTime: '',
	arrivalDate: '',
	arrivalTime: '',
	note: '',
	crewMember: null,
};

const useEditAssignmentForm = ({ onSubmit, defaultValues = DEFAULT_VALUES }) => {
	const {
		control,
		formState: { isValid },
		handleSubmit,
		getValues,
		trigger,
		watch,
	} = useForm({ mode: 'onChange', defaultValues });

	const maxAllowedPastDate = useMemo(() => getMaxAllowedPastDate(), []);

	const revalidateArrivalTime = useCallback(() => trigger('arrivalTime'), [trigger]);

	const departureDateRules = useMemo(
		() => ({
			required: true,
			validate: (value) => !isTodayOrFutureInputDate(value) || 'Only past dates are allowed.',
		}),
		[]
	);

	const arrivalDateRules = useMemo(
		() => ({
			required: true,
			validate: (value) => !isTodayOrFutureInputDate(value) || 'Only past dates are allowed.',
		}),
		[]
	);

	const arrivalTimeRules = useMemo(
		() => ({
			required: true,
			validate: (value) => {
				const { departureDate, departureTime, arrivalDate } = getValues();

				if (!departureDate || !departureTime || !arrivalDate || !value) return true;

				const departure = new Date(`${departureDate}T${departureTime}`);
				const arrival = new Date(`${arrivalDate}T${value}`);

				return arrival > departure || 'Arrival must be after departure.';
			},
		}),
		[getValues]
	);

	const handleFormSubmit = useCallback(
		async (values) => {
			const { crewMember, ...rest } = values;

			await onSubmit({ ...rest, crewMember, newCrewMemberId: crewMember?.id ?? '' });
		},
		[onSubmit]
	);

	return {
		control,
		maxAllowedPastDate,
		arrivalDateRules,
		arrivalTimeRules,
		departureDateRules,
		isSubmitDisabled: !isValid,
		departureDate: watch('departureDate'),
		revalidateArrivalTime,
		submitHandler: handleSubmit(handleFormSubmit),
	};
};

export default useEditAssignmentForm;
